import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { sendPasswordResetEmail } from 'firebase/auth';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const actionUrl = window.location.hostname === 'localhost' 
        ? 'https://artmosphere-89b64.web.app/reset-password'
        : `${window.location.origin}/reset-password`;
      
      const actionCodeSettings = {
        url: actionUrl,
        handleCodeInApp: false,
      };
      
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      setSuccess(true);
      setError('');
    } catch (error) {
      console.error('Password reset error:', error);
      
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address. Please check your email or sign up for a new account.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many password reset requests. Please try again later.');
      } else if (error.code === 'auth/invalid-continue-uri') {
        setError('Invalid redirect URL. Please contact support.');
      } else if (error.code === 'auth/unauthorized-continue-uri') {
        setError('Unauthorized redirect URL. Please contact support.');
      } else {
        setError(`Failed to send password reset email: ${error.message || 'Unknown error'}. Please try again or contact support.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-premium-page">
      <div className="auth-premium-card">
        <div className="auth-premium-gold-rule mb-6 rounded-full" />
        <div className="text-center mb-6">
          <p className="auth-premium-eyebrow mb-2">Artmosphere</p>
          <h1 className="auth-premium-title text-3xl mb-2">
            Reset Password
          </h1>
          <p className="auth-premium-subtitle text-sm">
            {success 
              ? "We've sent you a password reset link"
              : "Enter your email to receive a password reset link"
            }
          </p>
        </div>

        {!success ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="auth-premium-label mb-1 block">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-premium-input px-4 py-3"
                placeholder="your.email@example.com"
                required
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="art-btn-primary w-full rounded-xl px-4 py-3 font-semibold disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="auth-premium-link text-sm"
              >
                Back to Login
              </button>
            </div>
          </form>
        ) : (
          <div className="text-center space-y-4">
            <div className="auth-premium-callout-icon mx-auto flex h-16 w-16 items-center justify-center rounded-full">
              <svg className="h-8 w-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="auth-premium-title text-lg mb-2">Check your email</h3>
              <p className="auth-premium-subtitle text-sm mb-2">
                We've sent a password reset link to <strong className="text-[#2f251b]">{email}</strong>
              </p>
              <div className="art-callout-strip mt-3 p-3 text-left">
                <p className="text-xs font-medium text-[#5c4428] mb-1">Didn't receive the email?</p>
                <ul className="text-xs text-[#6b5a48] space-y-1 list-disc list-inside">
                  <li>Check your spam or junk folder</li>
                  <li>Wait a few minutes — emails can take up to 5 minutes</li>
                  <li>Make sure you entered the correct email address</li>
                </ul>
              </div>
              <p className="text-xs text-[#9a7a55] mt-3">
                Click the link in the email to reset your password. The link will expire in 1 hour.
              </p>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/login')}
                className="art-btn-primary w-full rounded-xl px-4 py-3 font-semibold"
              >
                Back to Login
              </button>
              <button
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                }}
                className="auth-premium-link w-full text-sm"
              >
                Send to a different email
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
